import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageShell, StatCard } from '../../components/Ui';
import { MdRestaurant, MdPerson, MdCheckCircle } from 'react-icons/md';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHmsConfig } from '../../hooks/useHmsRecords';

const DIET_TYPES = ['Normal', 'Diabetic', 'Low Sodium', 'Low Fat', 'High Protein', 'Liquid', 'Soft Diet', 'Renal Diet', 'Post-Op', 'NPO (Nothing by Mouth)'];
const MEAL_TYPES = ['Breakfast', 'Mid-Morning', 'Lunch', 'Afternoon Snack', 'Dinner', 'Bedtime'];

const DEFAULT_MENUS = {
  Normal: {
    Breakfast: 'Idli/Dosa + Sambar + Chutney + Tea/Coffee',
    'Mid-Morning': 'Fruits / Biscuits + Juice',
    Lunch: 'Rice + Dal + Sabzi + Curd + Chapati',
    'Afternoon Snack': 'Tea + Snack (Poha/Upma)',
    Dinner: 'Chapati + Dal + Sabzi + Rice',
    Bedtime: 'Milk (warm)',
  },
  Diabetic: {
    Breakfast: 'Oats/Idli (less rice) + Sambar + Tea (no sugar)',
    'Mid-Morning': 'Sprouts / Cucumber + Buttermilk',
    Lunch: 'Brown rice (small) + Dal + Green Sabzi + Salad',
    'Afternoon Snack': 'Roasted Chana / Nuts',
    Dinner: 'Multi-grain Chapati + Dal + Sabzi',
    Bedtime: 'Low-fat Milk (no sugar)',
  },
  Liquid: {
    Breakfast: 'Fruit Juice / Coconut Water',
    'Mid-Morning': 'Buttermilk / Lassi',
    Lunch: 'Clear Soup / Lentil Soup',
    'Afternoon Snack': 'Tender Coconut Water',
    Dinner: 'Daliya Soup / Vegetable Broth',
    Bedtime: 'Warm Milk',
  },
};

function orderMap(orders) {
  const m = {};
  (orders || []).forEach((o) => { m[o.admissionId] = o; });
  return m;
}

function printKitchenSheet(dietOrders, today) {
  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const win = window.open('', '_blank', 'width=900,height=650');
  if (!win) return;
  win.document.write('<html><head><title>Kitchen Sheet</title><style>body{font-family:Arial,sans-serif;max-width:860px;margin:20px auto}table{width:100%;border-collapse:collapse}th,td{padding:7px 10px;border:1px solid #e2e8f0;font-size:12px;text-align:left}th{background:#f1f5f9;font-weight:700}.title{font-size:18px;font-weight:800;margin-bottom:4px}.sub{font-size:12px;color:#64748b;margin-bottom:14px}@media print{body{margin:8px}}</style></head><body>');
  win.document.write('<div class="title">Kitchen Diet Sheet</div><div class="sub">Date: ' + esc(today) + ' · Total patients: ' + dietOrders.length + '</div>');
  win.document.write('<table><thead><tr><th>#</th><th>Patient</th><th>Ward / Bed</th><th>Diet Type</th><th>Breakfast</th><th>Lunch</th><th>Dinner</th><th>Allergy / Notes</th></tr></thead><tbody>');
  dietOrders.forEach((o, i) => {
    const npo = o.dietType === 'NPO (Nothing by Mouth)';
    win.document.write('<tr' + (npo ? ' style="background:#fef2f2"' : '') + '>');
    win.document.write('<td>' + (i + 1) + '</td>');
    win.document.write('<td><strong>' + esc(o.patientName) + '</strong></td>');
    win.document.write('<td>' + esc(o.ward) + ' · ' + esc(o.bed) + '</td>');
    win.document.write('<td><strong' + (npo ? ' style="color:#dc2626"' : '') + '>' + esc(o.dietType) + '</strong></td>');
    win.document.write('<td>' + (npo ? '—' : esc(o.mealDelivered?.[today + '|Breakfast'] ? '✓ Delivered' : 'Pending')) + '</td>');
    win.document.write('<td>' + (npo ? '—' : esc(o.mealDelivered?.[today + '|Lunch'] ? '✓ Delivered' : 'Pending')) + '</td>');
    win.document.write('<td>' + (npo ? '—' : esc(o.mealDelivered?.[today + '|Dinner'] ? '✓ Delivered' : 'Pending')) + '</td>');
    win.document.write('<td>' + esc((o.allergyNote ? o.allergyNote + ' ' : '') + (o.spInstruction || '')) + '</td>');
    win.document.write('</tr>');
  });
  if (!dietOrders.length) win.document.write('<tr><td colspan="8" style="text-align:center;color:#64748b">No diet orders.</td></tr>');
  win.document.write('</tbody></table></body></html>');
  win.document.close();
  win.print();
}

export default function DietManagementPage() {
  const { data, loading } = useLiveData(() => ipApi.getAdmissions(50), []);
  const { data: dietData, reload: reloadDiet } = useLiveData(() => ipApi.getDietOrders(100), []);
  const { config: menusConfig, save: saveMenusConfig } = useHmsConfig('diet-menus');
  const admissions = data?.admissions || [];
  const savedByAdm = orderMap(dietData?.orders);

  const [localOrders, setLocalOrders] = useState({});
  const [tab, setTab] = useState('orders');
  const [selectedDiet, setSelectedDiet] = useState('Normal');
  const [saving, setSaving] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [menuDraft, setMenuDraft] = useState({});
  const [menusSaving, setMenusSaving] = useState(false);
  const [menusMsg, setMenusMsg] = useState('');

  const today = new Date().toISOString().slice(0, 10);

  const effectiveMenus = useMemo(() => ({
    ...DEFAULT_MENUS,
    ...(menusConfig?.menus || {}),
  }), [menusConfig]);

  useEffect(() => {
    const next = {};
    admissions.forEach((adm) => {
      const saved = savedByAdm[adm.admissionId];
      next[adm.admissionId] = saved || {
        admissionId: adm.admissionId,
        patientName: adm.patientName,
        ward: adm.ward,
        bed: adm.bedLabel || adm.bedId,
        dietType: 'Normal',
        allergyNote: '',
        spInstruction: '',
        mealDelivered: {},
      };
    });
    setLocalOrders((prev) => ({ ...prev, ...next }));
  }, [admissions, dietData]);

  useEffect(() => {
    setMenuDraft({ ...(effectiveMenus[selectedDiet] || {}) });
  }, [selectedDiet, effectiveMenus]);

  const getOrder = (admId) => localOrders[admId] || {};

  const persistOrder = useCallback(async (admId, patch) => {
    const adm = admissions.find((a) => a.admissionId === admId);
    const merged = { ...getOrder(admId), ...patch, admissionId: admId };
    setLocalOrders((d) => ({ ...d, [admId]: merged }));
    setSaving(admId);
    setSaveMsg('');
    try {
      await ipApi.saveDietOrder(admId, {
        patientName: merged.patientName || adm?.patientName,
        ward: merged.ward || adm?.ward,
        bed: merged.bed || adm?.bedLabel || adm?.bedId,
        dietType: merged.dietType,
        allergyNote: merged.allergyNote,
        spInstruction: merged.spInstruction,
        mealDelivered: merged.mealDelivered,
      });
      await reloadDiet();
    } catch (e) {
      setSaveMsg(e.message);
    } finally {
      setSaving('');
    }
  }, [admissions, localOrders, reloadDiet]);

  const setDietOrder = (admId, patch) => {
    persistOrder(admId, patch);
  };

  const markMeal = (admId, meal) => {
    const order = getOrder(admId);
    const key = `${today}|${meal}`;
    const mealDelivered = { ...(order.mealDelivered || {}), [key]: !order.mealDelivered?.[key] };
    persistOrder(admId, { mealDelivered });
  };

  const dietOrders = admissions.map((a) => getOrder(a.admissionId)).filter((o) => o.admissionId);

  return (
    <PageShell
      title="Diet & Nutrition Management"
      subtitle="Inpatient diet orders · Meal planning · Delivery tracking (saved to database)"
    >
      {saveMsg && <div className="error mb">{saveMsg}</div>}
      <div className="stat-grid">
        <StatCard label="Patients with Diet Orders" value={dietOrders.length} accent="#10b981" icon={MdRestaurant} loading={loading} />
        <StatCard label="Special Diets" value={dietOrders.filter((d) => d.dietType !== 'Normal').length} accent="#f97316" icon={MdPerson} />
        <StatCard label="NPO Patients" value={dietOrders.filter((d) => d.dietType === 'NPO (Nothing by Mouth)').length} accent="#ef4444" icon={MdCheckCircle} />
        <StatCard label="Total Inpatients" value={admissions.length} accent="#0d6e6a" loading={loading} />
      </div>

      <div className="period-tabs card mb">
        {[
          { id: 'orders', label: 'Diet Orders' },
          { id: 'menu', label: 'Standard Menus' },
          { id: 'delivery', label: 'Meal Delivery' },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'orders' && (
        <div className="card">
          <h3>Diet Orders — All Inpatients</h3>
          <p className="hint mb">Changes save automatically to DynamoDB.</p>
          {admissions.length === 0 && !loading && <p className="hint table-empty">No inpatients found.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {admissions.map((adm) => {
              const order = getOrder(adm.admissionId);
              return (
                <div key={adm.admissionId} className="diet-order-card">
                  <div className="diet-card-head">
                    <div className="diet-patient-info">
                      <div className="diet-patient-name">{adm.patientName}</div>
                      <div className="diet-patient-meta">{adm.ward} · Bed {adm.bedLabel || adm.bedId} · Dr. {adm.doctor}</div>
                    </div>
                    <select
                      className="input"
                      style={{ width: 200, fontWeight: 600 }}
                      value={order.dietType || 'Normal'}
                      disabled={saving === adm.admissionId}
                      onChange={(e) => setDietOrder(adm.admissionId, { dietType: e.target.value })}
                    >
                      {DIET_TYPES.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="diet-card-fields">
                    <div className="field">
                      <label>Allergy / Intolerance</label>
                      <input className="input" placeholder="e.g. No eggs, No dairy" value={order.allergyNote || ''} onBlur={(e) => setDietOrder(adm.admissionId, { allergyNote: e.target.value })} onChange={(e) => setLocalOrders((d) => ({ ...d, [adm.admissionId]: { ...getOrder(adm.admissionId), allergyNote: e.target.value } }))} />
                    </div>
                    <div className="field">
                      <label>Special Instructions</label>
                      <input className="input" placeholder="e.g. Low spice, extra soft" value={order.spInstruction || ''} onBlur={(e) => setDietOrder(adm.admissionId, { spInstruction: e.target.value })} onChange={(e) => setLocalOrders((d) => ({ ...d, [adm.admissionId]: { ...getOrder(adm.admissionId), spInstruction: e.target.value } }))} />
                    </div>
                  </div>
                  {(order.dietType === 'NPO (Nothing by Mouth)') && (
                    <div className="diet-npo-banner">NPO — DO NOT SERVE FOOD OR LIQUIDS</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'menu' && (
        <div className="card">
          <div className="card-head-row">
            <h3>Standard Diet Menus</h3>
            <select className="input" style={{ width: 200 }} value={selectedDiet} onChange={(e) => setSelectedDiet(e.target.value)}>
              {DIET_TYPES.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          {menusMsg && <div className="info-banner mb">{menusMsg}</div>}
          <p className="hint mb">Edit meal lines below and click Save to persist for this hospital.</p>
          <div className="diet-menu-grid">
            {MEAL_TYPES.map((meal) => (
              <div key={meal} className="diet-menu-card">
                <div className="diet-meal-time">{meal}</div>
                <textarea
                  className="input"
                  rows={2}
                  style={{ fontSize: 12, marginTop: 4 }}
                  value={menuDraft[meal] ?? (effectiveMenus[selectedDiet]?.[meal] || '')}
                  onChange={(e) => setMenuDraft((d) => ({ ...d, [meal]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className="form-actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={menusSaving}
              onClick={async () => {
                setMenusSaving(true);
                setMenusMsg('');
                try {
                  const next = { ...effectiveMenus, [selectedDiet]: { ...menuDraft } };
                  await saveMenusConfig({ menus: next });
                  setMenusMsg(`Menus for "${selectedDiet}" saved.`);
                } catch (e) {
                  setMenusMsg(e.message || 'Could not save menus');
                } finally { setMenusSaving(false); }
              }}
            >{menusSaving ? 'Saving…' : 'Save menus'}</button>
          </div>
        </div>
      )}

      {tab === 'delivery' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0 }}>Meal Delivery Tracking — {today}</h3>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => printKitchenSheet(dietOrders, today)}>Print kitchen sheet</button>
          </div>
          <p className="hint mb">Mark each meal as delivered — auto-resets each new day.</p>
          <div className="diet-delivery-table-wrap">
            <table className="diet-delivery-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Ward / Bed</th>
                  <th>Diet Type</th>
                  {MEAL_TYPES.map((m) => <th key={m}>{m.split(' ')[0]}</th>)}
                </tr>
              </thead>
              <tbody>
                {dietOrders.map((order) => (
                  <tr key={order.admissionId}>
                    <td style={{ fontWeight: 600 }}>{order.patientName}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{order.ward} · {order.bed}</td>
                    <td>
                      <span style={{ fontSize: 11, fontWeight: 700, background: order.dietType === 'NPO (Nothing by Mouth)' ? '#fef2f2' : '#f0fdf4', color: order.dietType === 'NPO (Nothing by Mouth)' ? '#dc2626' : '#16a34a', padding: '2px 8px', borderRadius: 999 }}>
                        {order.dietType}
                      </span>
                    </td>
                    {MEAL_TYPES.map((m) => {
                      const key = `${today}|${m}`;
                      return (
                        <td key={m}>
                          {order.dietType === 'NPO (Nothing by Mouth)' ? (
                            <span style={{ color: '#ef4444', fontWeight: 800, fontSize: 14 }}>X</span>
                          ) : (
                            <button
                              type="button"
                              className="mar-cell-btn"
                              style={{ background: order.mealDelivered?.[key] ? '#16a34a' : '#f1f5f9', color: order.mealDelivered?.[key] ? '#fff' : '#94a3b8' }}
                              onClick={() => markMeal(order.admissionId, m)}
                            >
                              {order.mealDelivered?.[key] ? '✓' : '·'}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {dietOrders.length === 0 && <tr><td colSpan={MEAL_TYPES.length + 3} className="hint table-empty">No inpatients.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageShell>
  );
}
